import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  outputValue,
  outputLoading,
  outputResponseCode,
  outputResponseMessage,
  outputIncludeNonValidated,
  outputIncludeOutboundBeacons,
} from '../../redux/validation/validationResultsSlice';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Outlet, useNavigate } from 'react-router-dom';
import ComparisonAccordion from './ComparisonAccordion';
import BlockedAttributesSummary from './BlockedAttributesSummary';
import Pagination from '@mui/material/Pagination';
import { keyframes } from '@emotion/react';
import { Typography } from '@mui/material';

const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

export default function OutputHandler({
  historyMode = false,
  onContentReady,
  editableValidationState,
  onValidationStatusChange,
}) {
  const reduxOutput = useSelector(outputValue);

  const apiReturn = historyMode && editableValidationState ? editableValidationState : reduxOutput;
  const loading = useSelector(outputLoading);
  const responseCode = useSelector(outputResponseCode);
  const responseMessage = useSelector(outputResponseMessage);
  const includeNonValidated = useSelector(outputIncludeNonValidated);
  const includeOutboundBeacons = useSelector(outputIncludeOutboundBeacons);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [accordionsReady, setAccordionsReady] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const renderedCount = useRef(0);
  const timeoutRef = useRef(null);
  const spinnerStartTime = useRef(null);
  const prevIncludeNonValidated = useRef(null);
  const prevIncludeOutboundBeacons = useRef(null);
  const isCheckboxChange = useRef(false); // Track if current loading is from checkbox
  const contentReadyCalledRef = useRef(false); // Track if we've called the callback
  const itemsPerPage = 25;

  // Use the order from Redux (already correctly ordered with missing events in correct positions)
  const sortedApiReturn = useMemo(
    () => [...apiReturn], // Don't sort by timestamp - respect Redux ordering
    [apiReturn],
  );

  // Filter out 404s unless the box is checked
  // Filter out outbound beacons unless the box is checked
  // Missing events follow the same rules as regular events
  const filteredApiReturn = useMemo(() => {
    let filtered = sortedApiReturn;

    // Filter non-validated events if needed
    if (!includeNonValidated) {
      filtered = filtered.filter(
        (item) =>
          (item.isMissing && item.beaconType === 'inbound') || // Only show missing INBOUND events when non-validated is off
          (item.isMissing && item.beaconType !== 'inbound' && includeOutboundBeacons) || // Show missing outbound only if outbound filter is on
          (item.truth.status !== 404 && item.beaconData.status !== 404),
      );
    }

    // Filter outbound beacons if needed (including missing outbound events)
    if (!includeOutboundBeacons) {
      filtered = filtered.filter(
        (item) => item.beaconType === 'inbound', // Only show inbound beacons (including missing inbound)
      );
    }

    return filtered;
  }, [sortedApiReturn, includeNonValidated, includeOutboundBeacons]);

  const totalItems = filteredApiReturn.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredApiReturn.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Show spinner until accordions are ready
  const showSpinner = loading || isTransitioning || !accordionsReady;

  // Stable callback for accordion tracking with proper dependencies
  const handleAccordionRendered = useCallback(() => {
    // Skip if this is a checkbox change (using timeout instead)
    if (isCheckboxChange.current || accordionsReady) {
      return;
    }

    renderedCount.current += 1;

    if (renderedCount.current === paginatedData.length && paginatedData.length > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const elapsedTime = spinnerStartTime.current ? Date.now() - spinnerStartTime.current : 0;
      const minimumDelay = Math.max(300 - elapsedTime, 0);

      timeoutRef.current = setTimeout(() => {
        setAccordionsReady(true);
        setIsTransitioning(false);
        timeoutRef.current = null;
        spinnerStartTime.current = null;
      }, minimumDelay);
    }
  }, [accordionsReady, paginatedData.length]); // Only re-create when these change

  // Handle checkbox changes with simple timeout
  useEffect(() => {
    // Skip on initial mount
    if (prevIncludeNonValidated.current === null && prevIncludeOutboundBeacons.current === null) {
      prevIncludeNonValidated.current = includeNonValidated;
      prevIncludeOutboundBeacons.current = includeOutboundBeacons;
      return;
    }

    // Checkbox changed - use simple timeout
    if (
      (prevIncludeNonValidated.current !== includeNonValidated ||
        prevIncludeOutboundBeacons.current !== includeOutboundBeacons) &&
      !loading &&
      apiReturn.length > 0
    ) {
      // Force immediate state update to show spinner before pagination can render
      flushSync(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        isCheckboxChange.current = true; // Flag this as checkbox change
        setAccordionsReady(false);
        setIsTransitioning(true);
        setCurrentPage(1);
      });

      // Simple timeout for checkbox changes (no accordion counting needed)
      timeoutRef.current = setTimeout(() => {
        setAccordionsReady(true);
        setIsTransitioning(false);
        isCheckboxChange.current = false; // Reset flag
        timeoutRef.current = null;
      }, 400);
    }

    // Update tracking
    prevIncludeNonValidated.current = includeNonValidated;
    prevIncludeOutboundBeacons.current = includeOutboundBeacons;
  }, [includeNonValidated, includeOutboundBeacons, loading, apiReturn.length]);

  // Handle regular data changes - use accordion tracking for API loads and pagination
  useEffect(() => {
    // Only handle non-loading scenarios where checkbox didn't already trigger
    if (!loading && filteredApiReturn.length > 0 && !isTransitioning) {
      spinnerStartTime.current = Date.now();
      isCheckboxChange.current = false; // This is NOT a checkbox change
      setAccordionsReady(false);
      setIsTransitioning(true);
      renderedCount.current = 0;
    } else if (!loading && filteredApiReturn.length === 0) {
      setAccordionsReady(true);
      setIsTransitioning(false);
      renderedCount.current = 0;
      spinnerStartTime.current = null;
    }
  }, [filteredApiReturn.length, loading]);

  // Handle page changes
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    spinnerStartTime.current = Date.now();
    isCheckboxChange.current = false; // This is NOT a checkbox change
    setAccordionsReady(false);
    setIsTransitioning(true);
    renderedCount.current = 0;
  };

  // Reset states when API loading starts
  useEffect(() => {
    if (loading) {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setCurrentPage(1);
      setAccordionsReady(false);
      setIsTransitioning(false);
      renderedCount.current = 0;
      spinnerStartTime.current = null;
      isCheckboxChange.current = false; // Reset flag
    }
  }, [loading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      spinnerStartTime.current = null;
      contentReadyCalledRef.current = false;
    };
  }, []);

  // Call onContentReady callback when accordions are ready (for history mode)
  useEffect(() => {
    if (accordionsReady && onContentReady && !contentReadyCalledRef.current) {
      contentReadyCalledRef.current = true;
      onContentReady();
    }
  }, [accordionsReady, onContentReady]);

  // Redirect to home if there's no data and we're not loading
  // This handles page refreshes on /beacons
  useEffect(() => {
    if (!loading && apiReturn.length === 0 && !responseMessage) {
      // Small delay to avoid flash
      const redirectTimer = setTimeout(() => {
        navigate('/');
      }, 100);
      return () => clearTimeout(redirectTimer);
    }
  }, [loading, apiReturn.length, responseMessage, navigate]);

  return (
    <>
      {/* Spinner - show until accordions are ready */}
      {showSpinner && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20%',
            width: '100%',
          }}
        >
          <CircularProgress
            size="6rem"
            variant="determinate"
            value={70}
            sx={{
              color: 'secondary.main',
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          />
        </Box>
      )}

      {/* Content container - render but hide visually when spinner shows */}
      <div
        id="cardContainer"
        style={{
          visibility: showSpinner ? 'hidden' : 'visible',
          position: showSpinner ? 'absolute' : 'static',
          top: showSpinner ? '-9999px' : 'auto',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      >
        {responseCode === 200 && (
          <>
            <BlockedAttributesSummary validationResults={apiReturn} />
            {paginatedData.map((obj) => (
              <ComparisonAccordion
                key={obj.index}
                index={obj.index}
                beaconID={obj.beaconID}
                timestamp={obj.timestamp}
                apiDataTrue={obj.truth}
                apiData={obj.beaconData}
                modifiedRows={obj.modifiedRows}
                onRendered={handleAccordionRendered}
                beaconType={obj.beaconType}
                notes={obj.notes}
                manualBeaconStatus={obj.manualBeaconStatus}
                includeNonValidated={includeNonValidated}
                flowComparison={obj.flowComparison}
                eventName={obj.eventName}
                isMissing={obj.isMissing}
                hasOrderIssue={obj.hasOrderIssue}
                flowHasOrderIssue={obj.flowHasOrderIssue}
                actualSequence={obj.actualSequence}
                stepName={obj.stepName}
                stepNumber={obj.stepNumber}
                isBlockedEvent={obj.isBlockedEvent}
                // readOnly={historyMode}
                readOnly={historyMode ? false : false}
                onValidationStatusChange={onValidationStatusChange}
                historyMode={historyMode}
              />
            ))}
          </>
        )}
      </div>

      {/* Pagination - only show when not loading/transitioning */}
      {totalItems > itemsPerPage && !showSpinner && (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingBottom: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            sx={{
              '& .Mui-selected': {
                backgroundColor: (theme) => `${theme.palette.success.light} !important`,
                color: 'success.contrastText',
              },
            }}
          />
        </Box>
      )}

      {/* Error messages */}
      {!loading && (responseCode === 404 || responseCode >= 500) && responseMessage && (
        <Box
          sx={{
            display: 'flex',
            placeContent: 'center',
            marginTop: '10%',
            width: '100%',
            color: 'error.main',
          }}
        >
          <Typography sx={{ fontSize: 20 }}>{responseMessage}</Typography>
        </Box>
      )}

      <Outlet />
    </>
  );
}
